import { Divider, MenuItem } from "@material-ui/core";
import type { MouseEvent } from "react";
import { useGetEquipment } from "../../api/api";
import type { Id, StaticEquipment, Cable, OperationEquipment } from "../../types/models";

export const getLineStartingPoint = (cable: OperationEquipment): string => {
  const x = cable?.winch?.position_on_deck?.x;
  const y = cable?.winch?.position_on_deck?.y;
  return `${x ?? 0},${y ?? 0}`;
};

export const useGetGroupedEquipment = (shipId: Id, location: string): StaticEquipment[][] => {
  const { data: shipsEquipment = [] } = useGetEquipment(shipId);

  const equipmentForLocation = shipsEquipment.filter(
    (x) => x.direction === location
  );

  const grouped = equipmentForLocation.reduce<Record<string, StaticEquipment[]>>((acc, value) => {
    const key = `${value.position_on_deck?.x ?? ""},${value.position_on_deck?.y ?? ""}`;
    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key]!.push(value);

    return acc;
  }, {});

  return Object.values(grouped);
};

const stationTypeLabels = {
  winch: "Winch",
  mooring_bit: "Mooring Bit",
  spare: "Spare",
};

const stationTypeLabel = (type: string): string =>
  stationTypeLabels[type as keyof typeof stationTypeLabels] ?? type;

type PointComponentProps = {
  x: number;
  y: number;
  onClick: (event: MouseEvent) => void;
};

export const MenuItemsArrangements = ({
  arrangements,
  onArrangementSelect,
}: {
  arrangements: string[];
  onArrangementSelect: (arrangement: string) => void;
}) => {
  return (
    <div>
      <MenuItem disabled dense>
        Arrangement
      </MenuItem>
      {arrangements.map((arrangement) => {
        return (
          <MenuItem onClick={() => onArrangementSelect(arrangement)}>
            {arrangement}
          </MenuItem>
        );
      })}
    </div>
  );
};

export const MenuItemsCables = ({
  stations,
  disabledCableIds,
  spareCables,
  onCableSelect,
}: {
  stations: StaticEquipment[];
  disabledCableIds: Id[];
  spareCables: Cable[];
  onCableSelect: (cable: Cable, station: StaticEquipment) => void;
}) => {
  return (
  <>{stations.map((station) => {
    const cables: Cable[] =
      station?.type === "mooring_bit" ? spareCables || [] : station?.cables || [];

    return (
      <div>
        <MenuItem disabled dense>
          {stationTypeLabel(station?.type ?? "")} @ {station?.station_name}
        </MenuItem>
        {cables.map((cable) => {
          const disabled = disabledCableIds.includes(cable.id);
          return (
            <MenuItem
              disabled={disabled}
              onClick={() => {
                onCableSelect(cable, station);
              }}
            >
              {`${cable.tag} ${disabled ? "(in use)" : ""}`}
            </MenuItem>
          );
        })}
        <Divider />
      </div>
      );
    })}</>
    );
};

export const Circle = ({
  x,
  y,
  onClick,
}: PointComponentProps) => {
  return (
    <g pointerEvents="visible">
      <circle
        className="station"
        cx={x}
        cy={y}
        r="3"
        fill="gray"
        onClick={onClick}
      />
      <path
        fill="none"
        stroke="#CCCCCC"
        strokeWidth="0.5"
        d={`M${x - 2},${y} l4,0`}
      />
      <path
        fill="none"
        stroke="#CCCCCC"
        strokeWidth="0.5"
        d={`M${x},${y - 2} l0,4`}
      />
    </g>
  );
};

export const Winch = ({
  x,
  y,
  onClick,
}: PointComponentProps) => {
  return (
    <g pointerEvents="visible">
      <rect
        x={x - 3}
        y={y-1}
        width="6"
        height="2"
        fill="#777777" 
      />
      <ellipse 
        cx={x-2} 
        cy={y} 
        rx={1}
       ry={4} 
        fill="#777777" 
      />
      <ellipse 
        cx={x+2} 
        cy={y} 
        rx={1} 
        ry={4} 
        fill="#777777" 
      />
      {/* transparent hit area */}
      <rect
        x={x - 6}
        y={y - 6}
        width="12"
        height="12"
        fill="transparent"
        onClick={onClick}
      />
    </g>
  );
};

export const Spare = ({
  x,
  y,
  onClick,
}: PointComponentProps) => {
  return (
    <g pointerEvents="visible">
      <text
        x={x}
        y={y + 2}
        textAnchor="middle"
        fontSize="6"
        fontWeight="900"
        fill="#777777"
      >
        ?
      </text>
      {/* transparent hit area */}
      <rect
        x={x - 6}
        y={y - 6}
        width="12"
        height="12"
        fill="transparent"
        onClick={onClick}
      />
    </g>
  );
};

export const MooringBit = ({
  x,
  y,
  onClick,
}: PointComponentProps) => {
  return (
    <g pointerEvents="visible">
      <rect
        x={x - 3.4}
        y={y + 0.8}
        width="6.8"
        height="1.8"
        rx="0.5"
        fill="#777777"
      />
      <rect
        x={x - 2.5}
        y={y - 2}
        width="2"
        height="4"
        fill="#777777"
      />
      <rect
        x={x + 0.5}
        y={y - 2}
        width="2"
        height="4"
        fill="#777777"
      />
      {/* transparent hit area */}
      <rect
        x={x - 6}
        y={y - 6}
        width="12"
        height="12"
        fill="transparent"
        onClick={onClick}
      />
    </g>
  );
};
